<p-dialog
  [(visible)]="visible"
  [modal]="true"
  [closable]="false"
  [closeOnEscape]="false"
  [draggable]="false"
  [resizable]="false"
  [maximizable]="true"
  [blockScroll]="true"
  [style]="dialogStyle"
  [dt]="dialogTokens"
  appendTo="body"
>
  <ng-template pTemplate="header">
    <div class="flex items-start justify-between gap-3 w-full">
      <div class="flex flex-col">
        <h3 class="m-0">{{ title }}</h3>
        <span class="text-sm text-gray-500">
          {{ animal.displayName || animal.name }}
          @if (animal.bookNumber) { · {{ animal.bookNumber }} }
          @if (animal.category) { · {{ 'animal.category.' + animal.category | transloco }} }
        </span>
      </div>
      <p-button
        [text]="true"
        [rounded]="true"
        severity="secondary"
        icon="pi pi-times"
        [ariaLabel]="'modal.cancel' | transloco"
        [disabled]="saving"
        (onClick)="requestClose()"
      />
    </div>
  </ng-template>

  <form [formGroup]="form" class="flex flex-col gap-8" data-cy="medical-record-dialog" (ngSubmit)="save()">
    @if (correctionOf) {
      <p-message severity="info" styleClass="w-full">
        {{ 'animalMedical.dialog.correctionHint' | transloco: { id: correctionOf.id, date: (correctionDate | date:'dd.MM.yyyy HH:mm') } }}
      </p-message>
    }
    @if (isRetry) {
      <p-message severity="warn" styleClass="w-full">
        {{ 'animalMedical.dialog.retryHint' | transloco: { id: savedRecordId } }}
      </p-message>
    }

    <!-- Termin -->
    <section class="flex flex-col gap-3">
      <h4 class="medical-dialog-heading">{{ 'animalMedical.dialog.blocks.appointment' | transloco }}</h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="flex flex-col gap-1">
          <p-iftalabel>
            <p-datepicker
              formControlName="date"
              inputId="recordDate"
              dateFormat="dd.mm.yy"
              [showTime]="true"
              hourFormat="24"
              [showIcon]="true"
              iconDisplay="input"
              appendTo="body"
              [fluid]="true"
              [invalid]="submitted && form.controls.date.invalid"
            />
            <label for="recordDate">{{ 'animalMedical.fields.date' | transloco }} *</label>
          </p-iftalabel>
          @if (submitted && form.controls.date.invalid) {
            <small class="text-red-600">{{ 'animalMedical.dialog.required' | transloco }}</small>
          }
        </div>
        <p-iftalabel>
          <p-select
            formControlName="type"
            inputId="recordType"
            [options]="recordTypeOptions"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            styleClass="w-full"
          >
            <ng-template pTemplate="item" let-option>{{ option.label | transloco }}</ng-template>
            <ng-template pTemplate="selectedItem" let-option>{{ option?.label | transloco }}</ng-template>
          </p-select>
          <label for="recordType">{{ 'animalMedical.fields.type' | transloco }}</label>
        </p-iftalabel>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500" for="recordVeterinarian">{{ 'animalMedical.fields.veterinarian' | transloco }}</label>
          <app-veterinarian-select inputId="recordVeterinarian" formControlName="veterinarianId" />
        </div>
      </div>
    </section>

    <!-- Untersuchung -->
    <section class="flex flex-col gap-3">
      <h4 class="medical-dialog-heading">{{ 'animalMedical.dialog.blocks.examination' | transloco }}</h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <p-iftalabel>
          <p-inputnumber
            formControlName="weightKg"
            inputId="recordWeight"
            mode="decimal"
            locale="de-DE"
            [minFractionDigits]="0"
            [maxFractionDigits]="3"
            [min]="0"
            suffix=" kg"
            [fluid]="true"
          />
          <label for="recordWeight">{{ 'animalMedical.fields.weight' | transloco }}</label>
        </p-iftalabel>
      </div>
      <app-body-condition-score
        [careCtrl]="form.controls.careCondition"
        [nutritionCtrl]="form.controls.bcsNutrition"
      />
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <p-iftalabel>
          <textarea pTextarea id="recordFinding" formControlName="finding" rows="4" class="w-full"></textarea>
          <label for="recordFinding">{{ 'animalMedical.fields.finding' | transloco }}</label>
        </p-iftalabel>
        <p-iftalabel>
          <textarea pTextarea id="recordTreatment" formControlName="treatment" rows="4" class="w-full"></textarea>
          <label for="recordTreatment">{{ 'animalMedical.fields.treatment' | transloco }}</label>
        </p-iftalabel>
      </div>
    </section>

    <!-- Behandlungen -->
    <section class="flex flex-col gap-3">
      <div class="medical-dialog-heading" role="heading" aria-level="4">
        <span>{{ 'animalMedical.dialog.blocks.treatments' | transloco }}</span>
        @if (!isRetry) {
          <div class="flex flex-wrap gap-2">
            @for (category of [PreventiveTreatmentCategory.VACCINATION, PreventiveTreatmentCategory.ENDOPARASITE, PreventiveTreatmentCategory.ECTOPARASITE, PreventiveTreatmentCategory.MEDICATION]; track category) {
              <p-button
                size="small"
                [outlined]="true"
                icon="pi pi-plus"
                [label]="categoryLabelKey(category) | transloco"
                [attr.data-cy]="'medical-add-treatment-' + category"
                (onClick)="addTreatment(category)"
              />
            }
          </div>
        }
      </div>
      <div formArrayName="treatments" class="flex flex-col gap-3">
        @for (group of treatments; track group.controls.key.value; let i = $index) {
          @if (isPending(group.controls.key.value)) {
            <app-medical-treatment-fields
              [group]="group"
              [animalCategory]="animal.category"
              [recordDate]="recordDate"
              (remove)="removeTreatment(i)"
            />
          }
        }
      </div>
    </section>

    <!-- Diagnosen -->
    <section class="flex flex-col gap-3">
      <div class="medical-dialog-heading" role="heading" aria-level="4">
        <span>{{ 'animalMedical.dialog.blocks.conditions' | transloco }}</span>
        @if (!isRetry) {
          <p-button
            size="small"
            [outlined]="true"
            icon="pi pi-plus"
            [label]="'animalMedical.dialog.addCondition' | transloco"
            data-cy="medical-add-condition"
            (onClick)="addCondition()"
          />
        }
      </div>
      <div formArrayName="conditions" class="flex flex-col gap-3">
        @for (group of conditions; track group.controls.key.value; let i = $index) {
          @if (isPending(group.controls.key.value)) {
            <div [formGroup]="group" class="border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-cy="medical-condition">
              <p-iftalabel>
                <p-select
                  formControlName="kind"
                  [inputId]="'conditionKind-' + group.controls.key.value"
                  [options]="conditionKindOptions"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  styleClass="w-full"
                >
                  <ng-template pTemplate="item" let-option>{{ option.label | transloco }}</ng-template>
                  <ng-template pTemplate="selectedItem" let-option>{{ option?.label | transloco }}</ng-template>
                </p-select>
                <label [for]="'conditionKind-' + group.controls.key.value">{{ 'animalMedical.fields.conditionKind' | transloco }} *</label>
              </p-iftalabel>
              <div class="flex flex-col gap-1">
                <p-iftalabel>
                  <input
                    pInputText
                    [id]="'conditionLabel-' + group.controls.key.value"
                    formControlName="label"
                    class="w-full"
                    [class.ng-dirty]="submitted"
                  />
                  <label [for]="'conditionLabel-' + group.controls.key.value">{{ 'animalMedical.fields.conditionLabel' | transloco }} *</label>
                </p-iftalabel>
                @if ((submitted || group.controls.label.touched) && group.controls.label.invalid) {
                  <small class="text-red-600">{{ 'animalMedical.dialog.required' | transloco }}</small>
                }
              </div>
              <div class="flex items-start gap-2">
                <p-iftalabel class="flex-1">
                  <p-select
                    formControlName="severity"
                    [inputId]="'conditionSeverity-' + group.controls.key.value"
                    [options]="severityOptions"
                    optionLabel="label"
                    optionValue="value"
                    [showClear]="true"
                    appendTo="body"
                    styleClass="w-full"
                  >
                    <ng-template pTemplate="item" let-option>{{ option.label | transloco }}</ng-template>
                    <ng-template pTemplate="selectedItem" let-option>{{ option?.label | transloco }}</ng-template>
                  </p-select>
                  <label [for]="'conditionSeverity-' + group.controls.key.value">{{ 'animalMedical.fields.severity' | transloco }}</label>
                </p-iftalabel>
                @if (!isRetry) {
                  <p-button
                    size="small"
                    [text]="true"
                    severity="danger"
                    icon="pi pi-trash"
                    [ariaLabel]="'animalMedical.dialog.removePosition' | transloco"
                    (onClick)="removeCondition(i)"
                  />
                }
              </div>
              <p-iftalabel>
                <p-datepicker
                  formControlName="diagnosedAt"
                  [inputId]="'diagnosedAt-' + group.controls.key.value"
                  dateFormat="dd.mm.yy"
                  [showIcon]="true"
                  iconDisplay="input"
                  appendTo="body"
                  [fluid]="true"
                  (onSelect)="markDiagnosedAtManual(group)"
                  (onInput)="markDiagnosedAtManual(group)"
                />
                <label [for]="'diagnosedAt-' + group.controls.key.value">{{ 'animalMedical.fields.diagnosedAt' | transloco }}</label>
              </p-iftalabel>
              <div class="flex flex-col gap-1">
                <p-iftalabel>
                  <p-datepicker
                    formControlName="resolvedAt"
                    [inputId]="'resolvedAt-' + group.controls.key.value"
                    dateFormat="dd.mm.yy"
                    [showIcon]="true"
                    iconDisplay="input"
                    [showClear]="true"
                    appendTo="body"
                    [fluid]="true"
                  />
                  <label [for]="'resolvedAt-' + group.controls.key.value">{{ 'animalMedical.fields.resolvedAt' | transloco }}</label>
                </p-iftalabel>
                <small class="text-gray-500">{{ 'animalMedical.fields.resolvedAtHint' | transloco }}</small>
              </div>
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <p-toggleswitch formControlName="visibleToAdopter" [inputId]="'visibleToAdopter-' + group.controls.key.value" />
                  <label class="text-sm" [for]="'visibleToAdopter-' + group.controls.key.value">{{ 'animalMedical.fields.visibleToAdopter' | transloco }}</label>
                </div>
                <small class="text-orange-600">{{ 'animalMedical.fields.visibleToAdopterHint' | transloco }}</small>
              </div>
              <p-iftalabel class="md:col-span-3">
                <textarea pTextarea [id]="'conditionNotes-' + group.controls.key.value" formControlName="notes" rows="2" class="w-full"></textarea>
                <label [for]="'conditionNotes-' + group.controls.key.value">{{ 'animalMedical.fields.notes' | transloco }}</label>
              </p-iftalabel>
            </div>
          }
        }
      </div>
    </section>

    <!-- Quarantäne -->
    @if (!correctionOf && quarantinePending) {
      <section class="flex flex-col gap-3" formGroupName="quarantine">
        <div class="medical-dialog-heading">
          <span class="flex items-center gap-2">
            <p-toggleswitch
              inputId="quarantineEnabled"
              [formControl]="form.controls.quarantineEnabled"
              data-cy="medical-quarantine-toggle"
            />
            <label for="quarantineEnabled" class="cursor-pointer">{{ 'animalMedical.dialog.blocks.quarantine' | transloco }}</label>
          </span>
        </div>
        @if (form.controls.quarantineEnabled.value) {
          <div class="border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex flex-col gap-1">
              <p-iftalabel>
                <p-select
                  formControlName="reason"
                  inputId="quarantineReason"
                  [options]="reasonOptions"
                  optionLabel="label"
                  optionValue="value"
                  appendTo="body"
                  styleClass="w-full"
                  [invalid]="submitted && form.controls.quarantine.controls.reason.invalid"
                >
                  <ng-template pTemplate="item" let-option>{{ option.label | transloco }}</ng-template>
                  <ng-template pTemplate="selectedItem" let-option>{{ option?.label | transloco }}</ng-template>
                </p-select>
                <label for="quarantineReason">{{ 'animalMedical.fields.reason' | transloco }} *</label>
              </p-iftalabel>
              @if (submitted && form.controls.quarantine.controls.reason.invalid) {
                <small class="text-red-600">{{ 'animalMedical.dialog.required' | transloco }}</small>
              }
            </div>
            <p-iftalabel>
              <p-datepicker
                formControlName="startsAt"
                inputId="quarantineStartsAt"
                dateFormat="dd.mm.yy"
                [showIcon]="true"
                iconDisplay="input"
                appendTo="body"
                [fluid]="true"
                [invalid]="submitted && form.controls.quarantine.controls.startsAt.invalid"
                (onSelect)="markQuarantineStartManual()"
                (onInput)="markQuarantineStartManual()"
              />
              <label for="quarantineStartsAt">{{ 'animalMedical.fields.startsAt' | transloco }} *</label>
            </p-iftalabel>
            <div class="flex flex-col gap-1">
              <p-iftalabel>
                <p-datepicker
                  formControlName="endsAt"
                  inputId="quarantineEndsAt"
                  dateFormat="dd.mm.yy"
                  [showIcon]="true"
                  iconDisplay="input"
                  [showClear]="true"
                  appendTo="body"
                  [fluid]="true"
                  [invalid]="quarantineInvalidPeriod"
                />
                <label for="quarantineEndsAt">{{ 'animalMedical.fields.endsAt' | transloco }}</label>
              </p-iftalabel>
              <div class="flex items-center gap-2">
                <p-checkbox formControlName="unlimited" [binary]="true" inputId="quarantineUnlimited" />
                <label class="text-sm" for="quarantineUnlimited">{{ 'animalMedical.fields.unlimited' | transloco }}</label>
              </div>
              @if (quarantineInvalidPeriod) {
                <small class="text-red-600">{{ 'animalMedical.dialog.endBeforeStart' | transloco }}</small>
              }
            </div>
            <p-iftalabel>
              <p-select
                formControlName="authorityId"
                inputId="quarantineAuthority"
                [options]="authorityOptions"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                [filter]="true"
                appendTo="body"
                styleClass="w-full"
              />
              <label for="quarantineAuthority">{{ 'animalMedical.fields.authority' | transloco }}</label>
            </p-iftalabel>
            <div class="flex flex-col gap-1 md:col-span-2">
              <div class="flex items-center gap-2">
                <p-toggleswitch formControlName="blocksAdoption" inputId="quarantineBlocksAdoption" />
                <label class="text-sm" for="quarantineBlocksAdoption">{{ 'animalMedical.fields.blocksAdoption' | transloco }}</label>
              </div>
              <small class="text-gray-500">{{ 'animalMedical.fields.blocksAdoptionHint' | transloco }}</small>
            </div>
            <p-iftalabel class="md:col-span-3">
              <textarea pTextarea id="quarantineNotes" formControlName="notes" rows="2" class="w-full"></textarea>
              <label for="quarantineNotes">{{ 'animalMedical.fields.notes' | transloco }}</label>
            </p-iftalabel>
          </div>
        }
      </section>
    }

    <!-- Bestehendes fortschreiben -->
    @if (!correctionOf && visibleContinueCount > 0) {
      <section class="flex flex-col gap-2">
        <div class="flex flex-col gap-1">
          <h4 class="medical-dialog-heading">{{ 'animalMedical.dialog.blocks.continue' | transloco }}</h4>
          <span class="text-xs text-gray-500">{{ 'animalMedical.dialog.continueHint' | transloco }}</span>
        </div>
        <p-accordion [multiple]="true" [value]="[]">
          @if (continueConditions.length) {
            <p-accordion-panel value="conditions">
              <p-accordion-header>
                {{ 'animalMedical.dialog.continue.conditions' | transloco: { count: continueConditions.length } }}
              </p-accordion-header>
              <p-accordion-content>
                <div class="flex flex-col">
                  @for (item of continueConditions; track item.key) {
                    @if (isPending(item.key)) {
                      <div [formGroup]="item.form" class="flex flex-col lg:flex-row lg:items-center gap-3 px-2 py-3 border-b border-gray-100 last:border-b-0 text-sm"
                           [class.medical-changed]="isContinueConditionChanged(item)" data-cy="medical-continue-condition">
                        <span class="font-medium lg:flex-1 min-w-0">
                          {{ conditionKindLabelKey(item.original.kind) | transloco }} · {{ item.original.label }}
                        </span>
                        <div class="flex flex-wrap items-center gap-3">
                          <p-iftalabel class="w-48">
                            <p-datepicker
                              formControlName="resolvedAt"
                              [inputId]="item.key + '-resolvedAt'"
                              dateFormat="dd.mm.yy"
                              [showIcon]="true"
                              iconDisplay="input"
                              [showClear]="true"
                              appendTo="body"
                              [fluid]="true"
                            />
                            <label [for]="item.key + '-resolvedAt'">{{ 'animalMedical.fields.resolvedAt' | transloco }}</label>
                          </p-iftalabel>
                          <p-iftalabel class="w-48">
                            <p-select
                              formControlName="severity"
                              [inputId]="item.key + '-severity'"
                              [options]="severityOptions"
                              optionLabel="label"
                              optionValue="value"
                              [showClear]="true"
                              appendTo="body"
                              styleClass="w-full"
                            >
                              <ng-template pTemplate="item" let-option>{{ option.label | transloco }}</ng-template>
                              <ng-template pTemplate="selectedItem" let-option>{{ option?.label | transloco }}</ng-template>
                            </p-select>
                            <label [for]="item.key + '-severity'">{{ 'animalMedical.fields.severity' | transloco }}</label>
                          </p-iftalabel>
                          <div class="flex items-center gap-2">
                            <p-toggleswitch formControlName="visibleToAdopter" [inputId]="item.key + '-visible'" />
                            <label class="text-xs" [for]="item.key + '-visible'">{{ 'animalMedical.fields.visibleToAdopterShort' | transloco }}</label>
                          </div>
                        </div>
                      </div>
                    }
                  }
                </div>
              </p-accordion-content>
            </p-accordion-panel>
          }
          @if (continueQuarantines.length) {
            <p-accordion-panel value="quarantines">
              <p-accordion-header>
                {{ 'animalMedical.dialog.continue.quarantines' | transloco: { count: continueQuarantines.length } }}
              </p-accordion-header>
              <p-accordion-content>
                <div class="flex flex-col">
                  @for (item of continueQuarantines; track item.key) {
                    @if (isPending(item.key)) {
                      <div [formGroup]="item.form" class="flex flex-col lg:flex-row lg:items-center gap-3 px-2 py-3 border-b border-gray-100 last:border-b-0 text-sm"
                           [class.medical-changed]="isContinueQuarantineChanged(item)">
                        <span class="font-medium lg:flex-1 min-w-0">
                          {{ reasonLabelKey(item.original.reason) | transloco }}
                          <span class="text-xs text-gray-500">
                            {{ 'animalMedical.fields.since' | transloco }} {{ item.original.startsAt | date:'dd.MM.yyyy' }}
                            @if (isQuarantinePlanned(item.original)) { · {{ 'animalMedical.quarantine.planned' | transloco }} }
                          </span>
                        </span>
                        <div class="flex flex-wrap items-center gap-3">
                          <p-iftalabel class="w-48">
                            <p-datepicker
                              formControlName="endsAt"
                              [inputId]="item.key + '-endsAt'"
                              dateFormat="dd.mm.yy"
                              [showIcon]="true"
                              iconDisplay="input"
                              appendTo="body"
                              [fluid]="true"
                            />
                            <label [for]="item.key + '-endsAt'">{{ 'animalMedical.fields.endsAt' | transloco }}</label>
                          </p-iftalabel>
                          <div class="flex items-center gap-2">
                            <p-checkbox formControlName="unlimited" [binary]="true" [inputId]="item.key + '-unlimited'"
                                        (onChange)="onContinueQuarantineUnlimited(item)" />
                            <label class="text-xs" [for]="item.key + '-unlimited'">{{ 'animalMedical.fields.unlimited' | transloco }}</label>
                          </div>
                          <div class="flex items-center gap-2">
                            <p-toggleswitch formControlName="blocksAdoption" [inputId]="item.key + '-blocks'" />
                            <label class="text-xs" [for]="item.key + '-blocks'">{{ 'animalMedical.fields.blocksAdoptionShort' | transloco }}</label>
                          </div>
                          <p-button size="small" [text]="true" icon="pi pi-stop-circle"
                                    [label]="'animalMedical.dialog.continue.end' | transloco"
                                    (onClick)="endQuarantine(item)" />
                        </div>
                      </div>
                    }
                  }
                </div>
              </p-accordion-content>
            </p-accordion-panel>
          }
          @if (continueTreatments.length) {
            <p-accordion-panel value="treatments">
              <p-accordion-header>
                {{ 'animalMedical.dialog.continue.treatments' | transloco: { count: continueTreatments.length } }}
              </p-accordion-header>
              <p-accordion-content>
                <div class="flex flex-col">
                  @for (item of continueTreatments; track item.key) {
                    @if (isPending(item.key)) {
                      <div [formGroup]="item.form" class="flex flex-col lg:flex-row lg:items-center gap-3 px-2 py-3 border-b border-gray-100 last:border-b-0 text-sm"
                           [class.medical-changed]="isContinueTreatmentChanged(item)">
                        <span class="font-medium lg:flex-1 min-w-0">
                          {{ categoryLabelKey(item.original.category) | transloco }}
                          @for (type of item.original.types ?? []; track type; let first = $first) {
                            {{ first ? '·' : ',' }} {{ typeLabelKey(type) | transloco }}
                          }
                          <span class="text-xs text-gray-500">
                            {{ 'animalMedical.fields.administeredAt' | transloco }} {{ item.original.administeredAt | date:'dd.MM.yyyy' }}
                          </span>
                        </span>
                        <div class="flex flex-wrap items-center gap-3">
                          <p-iftalabel class="w-48">
                            <p-datepicker
                              formControlName="nextDueAt"
                              [inputId]="item.key + '-nextDueAt'"
                              dateFormat="dd.mm.yy"
                              [showIcon]="true"
                              iconDisplay="input"
                              appendTo="body"
                              [fluid]="true"
                            />
                            <label [for]="item.key + '-nextDueAt'">{{ 'animalMedical.fields.nextDueAt' | transloco }}</label>
                          </p-iftalabel>
                          <div class="flex items-center gap-2">
                            <p-checkbox formControlName="noFurtherDue" [binary]="true" [inputId]="item.key + '-none'"
                                        (onChange)="onContinueTreatmentNoFurtherDue(item)" />
                            <label class="text-xs" [for]="item.key + '-none'">{{ 'animalMedical.fields.noFurtherDueShort' | transloco }}</label>
                          </div>
                        </div>
                      </div>
                    }
                  }
                </div>
              </p-accordion-content>
            </p-accordion-panel>
          }
        </p-accordion>
      </section>
    }

    <!-- Anhänge -->
    <section class="flex flex-col gap-3">
      <h4 class="medical-dialog-heading">{{ 'animalMedical.dialog.blocks.attachments' | transloco }}</h4>

      @if (correctionFiles.length) {
        <div class="flex flex-wrap items-center gap-2 text-sm">
          <span class="text-xs text-gray-500">{{ 'animalMedical.dialog.attachments.keptOnOld' | transloco: { id: correctionOf?.id } }}</span>
          @for (file of correctionFiles; track file.key) {
            <button type="button"
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gray-300 text-xs hover:bg-gray-100 cursor-pointer"
                    (click)="openFile.emit(file)">
              <i class="pi pi-paperclip"></i>{{ file.name }}
            </button>
          }
        </div>
      }

      @if (!isRetry) {
        <label
          class="flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg p-4 text-sm text-gray-500 cursor-pointer transition-colors"
          [ngClass]="dragOver ? 'border-[var(--p-primary-400)] bg-[var(--p-primary-50)]' : 'border-gray-300 hover:bg-gray-50'"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave()"
          (drop)="onDrop($event)"
        >
          <i class="pi pi-cloud-upload text-2xl"></i>
          <span>{{ 'animalMedical.dialog.attachments.drop' | transloco }}</span>
          <span class="text-xs">{{ 'animalMedical.dialog.attachments.formats' | transloco }}</span>
          <input type="file" class="hidden" multiple [accept]="acceptTypes" (change)="onFileInput($event)" data-cy="medical-attachment-input" />
        </label>
      }

      @for (row of attachments; track row.key; let i = $index) {
        <div class="grid grid-cols-[3rem_minmax(0,1fr)_12rem_11rem_auto] gap-3 items-center border border-gray-200 rounded-lg p-2 text-sm">
          <div class="w-12 h-12 flex items-center justify-center rounded bg-gray-100 overflow-hidden">
            @if (row.previewUrl) {
              <img [src]="row.previewUrl" alt="" class="w-full h-full object-cover" />
            } @else {
              <i [class]="fileIcon(row) + ' text-xl'"></i>
            }
          </div>
          <div class="flex flex-col min-w-0">
            <span class="truncate font-medium">{{ row.file.name }}</span>
            <span class="text-xs text-gray-500">
              {{ row.file.size | fileSize }} ·
              {{ (row.target === 'document' ? 'animalMedical.dialog.attachments.toDocuments' : 'animalMedical.dialog.attachments.toMedia') | transloco }}
            </span>
          </div>
          @if (row.target === 'document') {
            <p-select
              [(ngModel)]="row.category"
              [ngModelOptions]="{ standalone: true }"
              [options]="documentCategoryOptions"
              optionLabel="label"
              optionValue="value"
              appendTo="body"
              styleClass="w-full"
              [disabled]="isRetry"
            >
              <ng-template pTemplate="item" let-option>{{ option.label | transloco }}</ng-template>
              <ng-template pTemplate="selectedItem" let-option>{{ option?.label | transloco }}</ng-template>
            </p-select>
          } @else {
            <p-tag severity="secondary" [value]="'animal.imageCategory.medical' | transloco" />
          }
          <p-datepicker
            [(ngModel)]="row.date"
            [ngModelOptions]="{ standalone: true }"
            dateFormat="dd.mm.yy"
            [showIcon]="true"
            iconDisplay="input"
            appendTo="body"
            [fluid]="true"
            [disabled]="isRetry"
            (onSelect)="markAttachmentDateManual(row)"
          />
          <p-button
            size="small"
            [text]="true"
            severity="danger"
            icon="pi pi-trash"
            [disabled]="isRetry"
            [ariaLabel]="'animalMedical.dialog.removePosition' | transloco"
            (onClick)="removeAttachment(i)"
          />
        </div>
      }
    </section>
  </form>

  <ng-template pTemplate="footer">
    <div class="flex flex-col gap-2 w-full border-t border-gray-200 pt-4">
      @if (uploadProgress) {
        <div class="flex items-center gap-3">
          <p-progressbar class="flex-1" [value]="uploadProgress.total ? (uploadProgress.done / uploadProgress.total) * 100 : 0" [showValue]="false" />
          <span class="text-xs text-gray-500 whitespace-nowrap">
            {{ 'animalMedical.dialog.uploadProgress' | transloco: { done: uploadProgress.done, total: uploadProgress.total } }}
          </span>
        </div>
      }
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-col text-left text-sm">
          <span>
            {{ (isRetry ? 'animalMedical.dialog.summary.retryPrefix' : 'animalMedical.dialog.summary.prefix') | transloco: { id: savedRecordId } }}
            @if (!isRetry) { {{ 'animalMedical.dialog.summary.record' | transloco }} }
            @if (summary.treatments) { · {{ 'animalMedical.dialog.summary.treatments' | transloco: { count: summary.treatments } }} }
            @if (summary.conditions) { · {{ 'animalMedical.dialog.summary.conditions' | transloco: { count: summary.conditions } }} }
            @if (summary.quarantine) { · {{ 'animalMedical.dialog.summary.quarantine' | transloco }} }
            @if (summary.changes) { · {{ 'animalMedical.dialog.summary.changes' | transloco: { count: summary.changes } }} }
            @if (summary.deletions) { · {{ 'animalMedical.dialog.summary.deletions' | transloco: { count: summary.deletions } }} }
            @if (summary.attachments) { · {{ 'animalMedical.dialog.summary.attachments' | transloco: { count: summary.attachments } }} }
          </span>
          <span class="text-xs text-gray-500">{{ 'animalMedical.dialog.summary.immutable' | transloco }}</span>
        </div>
        <div class="flex gap-2">
          <p-button
            icon="pi pi-times"
            [text]="true"
            [label]="'modal.cancel' | transloco"
            [disabled]="saving"
            (onClick)="requestClose()"
          />
          <p-button
            [icon]="isRetry ? 'pi pi-refresh' : 'pi pi-save'"
            [label]="(isRetry ? 'animalMedical.dialog.retry' : 'modal.save') | transloco"
            [loading]="saving"
            [disabled]="saving"
            data-cy="medical-record-save"
            (onClick)="save()"
          />
        </div>
      </div>
    </div>
  </ng-template>
</p-dialog>
